import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuthenticatedImage } from '../../../hooks/useAuthenticatedImage';
import { ImageObject } from '../../../utils/getImageUrl';
import { FaceGuide } from '../../liveness-challenge/ui/FaceGuide';
import { FaceComparer } from '../FaceComparer';
import { useSendFaceCompareLogMutation } from '../identityVerificationApiSlice';
import {
  VerificationAttempt,
  VerificationResult,
  VerificationStatus,
} from '../types';

async function blobUrlToDataUrl(blobUrl: string): Promise<string | null> {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const DEFAULT_MAX_ATTEMPTS = 3;
const ALIGNMENT_THRESHOLD = 0.65;
const ALIGNMENT_STABLE_MS = 700;
const MISS_TOLERANCE = 4;
const DETECTION_INTERVAL_MS = 200;
const MANUAL_CAPTURE_TIMEOUT_MS = 12000;

interface IdentityVerificationScreenProps {
  candidateId: string;
  certificationLogId: string;
  profileImageItem: ImageObject | string | null | undefined;
  stepLabel?: string;
  minAttempts?: number | null;
  allowPassOnAttemptLimit?: boolean | null;
  onVerified: (result: VerificationResult) => void;
  onBack?: () => void;
}

function computeAlignmentProgress(
  box: { x: number; y: number; width: number; height: number } | null,
): number {
  if (!box) return 0;

  const centerX = box.x + box.width / 2;
  const centerY = box.y + box.height / 2;

  // How close to frame center (normalized 0-1)
  const dx = centerX - 0.5;
  const dy = centerY - 0.5;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const centerScore = Math.max(0, 1 - dist * 2.5);

  // Face width between 20% and 80% of frame (relaxed)
  const inSizeRange = box.width >= 0.2 && box.width <= 0.8;
  const sizeScore = inSizeRange ? 1 : 0;

  return centerScore * sizeScore;
}

function getStatusCopy(status: VerificationStatus): string {
  switch (status) {
    case 'loading_models':
      return 'Carregando modelos de reconhecimento facial...';
    case 'awaiting_selfie':
      return 'Posicione o rosto dentro do enquadramento oval para iniciar a verificação automática.';
    case 'comparing':
      return 'Aguarde enquanto verificamos sua identidade...';
    case 'failed':
      return 'Rosto não reconhecido. Reposicione e tente novamente.';
    case 'no_profile_photo':
      return 'Cadastre uma foto no perfil e clique em "Tentar novamente".';
    case 'blocked':
      return 'Verificação bloqueada após 3 tentativas.';
    default:
      return '';
  }
}

function getProfileSourceKey(
  item: ImageObject | string | null | undefined,
): string | null {
  if (!item) return null;
  if (typeof item === 'string') return `url:${item}`;
  if ('_id' in item && item._id) return `_id:${item._id}`;
  if ('url' in item && item.url) return `url:${item.url}`;
  return null;
}

export function IdentityVerificationScreen({
  candidateId,
  certificationLogId,
  profileImageItem,
  stepLabel = 'Etapa 1 de 3',
  minAttempts,
  allowPassOnAttemptLimit,
  onVerified,
  onBack,
}: IdentityVerificationScreenProps) {
  const navigate = useNavigate();
  const [sendFaceCompareLog] = useSendFaceCompareLogMutation();
  const [status, setStatus] = useState<VerificationStatus>('loading_models');
  const maxAttempts =
    typeof minAttempts === 'number' && minAttempts > 0
      ? minAttempts
      : DEFAULT_MAX_ATTEMPTS;
  const allowPassOnLimit = allowPassOnAttemptLimit === true;
  const [attempts, setAttempts] = useState<VerificationAttempt[]>([]);
  const [alignmentProgress, setAlignmentProgress] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [showManualCapture, setShowManualCapture] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const comparerRef = useRef<FaceComparer | null>(null);
  const alignmentStableFrom = useRef<number | null>(null);
  const missStreakRef = useRef(0);
  const detectionLoopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isComparingRef = useRef(false);
  const stableProfileBlobUrlRef = useRef('');
  const stableProfileSourceKeyRef = useRef<string | null>(null);
  const stableCandidateIdRef = useRef(candidateId);

  const profileBlobUrl = useAuthenticatedImage(profileImageItem);
  const resolvedProfileBlobUrl =
    profileBlobUrl || stableProfileBlobUrlRef.current;
  const profileSourceKey = getProfileSourceKey(profileImageItem);

  useEffect(() => {
    if (stableCandidateIdRef.current === candidateId) return;

    stableCandidateIdRef.current = candidateId;
    stableProfileBlobUrlRef.current = '';
    stableProfileSourceKeyRef.current = null;
  }, [candidateId]);

  useEffect(() => {
    if (profileSourceKey) {
      if (stableProfileSourceKeyRef.current !== profileSourceKey) {
        stableProfileSourceKeyRef.current = profileSourceKey;
        stableProfileBlobUrlRef.current = '';
      }
    }

    if (profileBlobUrl && stableProfileSourceKeyRef.current) {
      stableProfileBlobUrlRef.current = profileBlobUrl;
    }
  }, [profileBlobUrl, profileSourceKey]);

  // Initialize models
  useEffect(() => {
    comparerRef.current = new FaceComparer();
    comparerRef.current
      .initialize()
      .then(() => {
        setStatus(prev =>
          prev === 'loading_models' ? 'awaiting_selfie' : prev,
        );
      })
      .catch(() => {
        setStatus('blocked');
      });
  }, []);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  // Open camera
  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user', width: 640, height: 480 } })
      .then(s => {
        streamRef.current = s;
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
        }
      })
      .catch(() => {
        setStatus('blocked');
      });

    return () => {
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    };
  }, []);

  // Detect no profile photo
  useEffect(() => {
    if (status !== 'awaiting_selfie') return;

    const hasProfilePhotoReference = Boolean(
      profileSourceKey || stableProfileSourceKeyRef.current,
    );
    const hasResolvedProfilePhoto = Boolean(resolvedProfileBlobUrl);

    if (!hasProfilePhotoReference && !hasResolvedProfilePhoto) {
      setStatus('no_profile_photo');
    }
  }, [profileSourceKey, resolvedProfileBlobUrl, status]);

  const triggerCapture = useCallback(async () => {
    if (isComparingRef.current) return;
    if (!comparerRef.current || !videoRef.current || !canvasRef.current) return;
    const profilePhotoUrl = resolvedProfileBlobUrl;
    if (!profilePhotoUrl) {
      setStatus('no_profile_photo');
      return;
    }

    isComparingRef.current = true;
    setShowManualCapture(false);
    if (detectionLoopRef.current) clearTimeout(detectionLoopRef.current);

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const captureFrame = (): string | null => {
      if (!videoRef.current || !canvasRef.current) return null;
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0);
      return canvas.toDataURL('image/jpeg', 0.92);
    };

    const frame1 = captureFrame();
    if (!frame1) {
      isComparingRef.current = false;
      return;
    }

    // Fire-and-forget: envia o par (foto de perfil, selfie capturada) em base64
    // para o log de comparação facial. Não bloqueia o fluxo de verificação.
    blobUrlToDataUrl(profilePhotoUrl)
      .then(profileDataUrl => {
        if (!profileDataUrl) return;
        sendFaceCompareLog({
          photo1Url: profileDataUrl,
          photo2Url: frame1,
        })
          .unwrap()
          .catch(() => {
            /* noop */
          });
      })
      .catch(() => {
        /* noop */
      });

    setStatus('comparing');
    setAlignmentProgress(0);

    const frame2 = await new Promise<string | null>(resolve => {
      setTimeout(() => resolve(captureFrame()), 300);
    });
    const frame3 = await new Promise<string | null>(resolve => {
      setTimeout(() => resolve(captureFrame()), 300);
    });

    const selfieFrames = [frame1, frame2, frame3].filter(
      (f): f is string => f !== null,
    );

    const attemptNumber = attempts.length + 1;

    try {
      const { match, distance } = await comparerRef.current.compare(
        profilePhotoUrl,
        selfieFrames,
      );

      const attempt: VerificationAttempt = {
        attemptNumber,
        status: match ? 'passed' : 'failed',
        distance,
        capturedAt: new Date().toISOString(),
      };

      const nextAttempts = [...attempts, attempt];

      if (match) {
        setAttempts(nextAttempts);
        setStatus('passed');
        const result: VerificationResult = {
          candidateId,
          certificationLogId,
          status: 'passed',
          attempts: nextAttempts,
          completedAt: new Date().toISOString(),
          capturedImageDataUrl: frame1,
        };
        onVerified(result);
        return;
      }

      if (nextAttempts.length >= maxAttempts) {
        setAttempts(nextAttempts);
        if (allowPassOnLimit) {
          setStatus('passed');
          const result: VerificationResult = {
            candidateId,
            certificationLogId,
            status: 'passed',
            attempts: nextAttempts,
            releasedByAttemptLimit: true,
            completedAt: new Date().toISOString(),
            capturedImageDataUrl: frame1,
          };
          onVerified(result);
          return;
        }
        setStatus('blocked');
        const result: VerificationResult = {
          candidateId,
          certificationLogId,
          status: 'blocked',
          attempts: nextAttempts,
          failureReason: 'max_retries_exceeded',
          completedAt: new Date().toISOString(),
          capturedImageDataUrl: frame1,
        };
        onVerified(result);
        return;
      }

      setAttempts(nextAttempts);
      setStatus('failed');
    } catch (err) {
      const { message } = err as Error;
      const attempt: VerificationAttempt = {
        attemptNumber,
        status: 'failed',
        distance: 1,
        capturedAt: new Date().toISOString(),
      };
      const nextAttempts = [...attempts, attempt];

      if (nextAttempts.length >= maxAttempts) {
        setAttempts(nextAttempts);
        if (allowPassOnLimit) {
          setStatus('passed');
          const result: VerificationResult = {
            candidateId,
            certificationLogId,
            status: 'passed',
            attempts: nextAttempts,
            releasedByAttemptLimit: true,
            completedAt: new Date().toISOString(),
            capturedImageDataUrl: frame1,
          };
          onVerified(result);
          return;
        }
        setStatus('blocked');
        const result: VerificationResult = {
          candidateId,
          certificationLogId,
          status: 'blocked',
          attempts: nextAttempts,
          failureReason:
            message === 'no_face_in_profile'
              ? 'no_profile_photo'
              : 'comparison_error',
          completedAt: new Date().toISOString(),
          capturedImageDataUrl: frame1,
        };
        onVerified(result);
        return;
      }

      setAttempts(nextAttempts);
      setStatus('failed');
    } finally {
      isComparingRef.current = false;
    }
  }, [
    allowPassOnLimit,
    attempts,
    candidateId,
    certificationLogId,
    maxAttempts,
    onVerified,
    resolvedProfileBlobUrl,
    sendFaceCompareLog,
  ]);

  // Face alignment detection loop
  useEffect(() => {
    if (status !== 'awaiting_selfie' && status !== 'failed') return;
    if (!comparerRef.current) return;

    const runDetection = async () => {
      if (isComparingRef.current) return;

      const video = videoRef.current;
      if (!video || video.readyState < 2) {
        detectionLoopRef.current = setTimeout(
          runDetection,
          DETECTION_INTERVAL_MS,
        );
        return;
      }

      try {
        const faceapi = await import('face-api.js');
        const options = new faceapi.TinyFaceDetectorOptions({
          inputSize: 320,
          scoreThreshold: 0.3,
        });
        const detection = await faceapi.detectSingleFace(video, options);

        if (detection) {
          const vw = video.videoWidth || video.clientWidth || 1;
          const vh = video.videoHeight || video.clientHeight || 1;
          const box = {
            x: detection.box.x / vw,
            y: detection.box.y / vh,
            width: detection.box.width / vw,
            height: detection.box.height / vh,
          };
          const progress = computeAlignmentProgress(box);
          setAlignmentProgress(progress);

          if (progress >= ALIGNMENT_THRESHOLD) {
            missStreakRef.current = 0;
            const now = Date.now();
            if (!alignmentStableFrom.current) {
              alignmentStableFrom.current = now;
            } else if (
              now - alignmentStableFrom.current >=
              ALIGNMENT_STABLE_MS
            ) {
              alignmentStableFrom.current = null;
              missStreakRef.current = 0;
              triggerCapture();
              return;
            }
          } else {
            missStreakRef.current += 1;
            if (missStreakRef.current >= MISS_TOLERANCE) {
              alignmentStableFrom.current = null;
            }
          }
        } else {
          missStreakRef.current += 1;
          if (missStreakRef.current >= MISS_TOLERANCE) {
            setAlignmentProgress(0);
            alignmentStableFrom.current = null;
          }
        }
      } catch {
        setAlignmentProgress(0);
      }

      detectionLoopRef.current = setTimeout(
        runDetection,
        DETECTION_INTERVAL_MS,
      );
    };

    alignmentStableFrom.current = null;
    missStreakRef.current = 0;
    detectionLoopRef.current = setTimeout(runDetection, DETECTION_INTERVAL_MS);

    const manualCaptureTimer = setTimeout(() => {
      setShowManualCapture(true);
    }, MANUAL_CAPTURE_TIMEOUT_MS);

    return () => {
      if (detectionLoopRef.current) clearTimeout(detectionLoopRef.current);
      clearTimeout(manualCaptureTimer);
    };
  }, [status, triggerCapture]);

  const isBlocked = status === 'blocked';
  const isNoPhoto = status === 'no_profile_photo';
  const isComparing = status === 'comparing';
  const isAwaitingCapture = status === 'awaiting_selfie' || status === 'failed';
  const showProfilePreview = status !== 'no_profile_photo';
  const showManualCaptureButton = showManualCapture && isAwaitingCapture;
  const remainingAttempts = Math.max(maxAttempts - attempts.length, 0);
  const statusCopy = getStatusCopy(status);
  let badgeLabel = 'Aguardando';
  if (isComparing) badgeLabel = 'Verificando';
  else if (isBlocked) badgeLabel = 'Bloqueado';
  else if (isNoPhoto) badgeLabel = 'Sem foto';
  const renderStatusSpecifics = (compact = false) => (
    <>
      {status === 'loading_models' && (
        <div className="flex items-center gap-3 text-slate-400">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-slate-300" />
          <p className={compact ? 'text-xs' : 'text-sm'}>
            Carregando modelos...
          </p>
        </div>
      )}

      {status === 'no_profile_photo' && (
        <div
          className={`rounded-2xl border border-amber-400/20 bg-amber-400/10 ${
            compact
              ? 'p-3 text-xs text-amber-200'
              : 'p-4 text-sm text-amber-200'
          }`}
        >
          <p className="font-semibold">Foto de perfil não encontrada</p>
          <p
            className={`mt-1 leading-5 text-amber-300/80 ${
              compact ? 'text-[11px]' : ''
            }`}
          >
            Cadastre uma foto no seu perfil e volte aqui para tentar novamente.
          </p>
          <Link
            to="/profile"
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-3 flex items-center gap-1.5 text-amber-300 underline underline-offset-2 hover:text-amber-100 ${
              compact ? 'text-xs' : ''
            }`}
          >
            Ir para meu perfil
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
            >
              <path
                fillRule="evenodd"
                d="M4.25 5.5a.75.75 0 0 0-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 0 0 .75-.75v-4a.75.75 0 0 1 1.5 0v4A2.25 2.25 0 0 1 12.75 17h-8.5A2.25 2.25 0 0 1 2 14.75v-8.5A2.25 2.25 0 0 1 4.25 4h5a.75.75 0 0 1 0 1.5h-5Z"
                clipRule="evenodd"
              />
              <path
                fillRule="evenodd"
                d="M6.194 12.753a.75.75 0 0 0 1.06.053L16.5 4.44v2.81a.75.75 0 0 0 1.5 0v-4.5a.75.75 0 0 0-.75-.75h-4.5a.75.75 0 0 0 0 1.5h2.553l-9.056 8.194a.75.75 0 0 0-.053 1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </Link>
          <button
            type="button"
            className={`mt-3 w-full rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 font-semibold text-amber-200 transition hover:bg-amber-400/20 ${
              compact ? 'py-2 text-xs' : 'py-2.5 text-sm'
            }`}
            onClick={() => navigate(0)}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {status === 'blocked' && (
        <div
          className={`rounded-2xl border border-red-400/20 bg-red-400/10 ${
            compact ? 'p-3 text-xs text-red-200' : 'p-4 text-sm text-red-200'
          }`}
        >
          <p className="font-semibold">Verificação bloqueada</p>
          <p
            className={`mt-1 leading-5 text-red-300/80 ${
              compact ? 'text-[11px]' : ''
            }`}
          >
            Não foi possível verificar sua identidade após {maxAttempts}{' '}
            tentativas.
          </p>
          <Link
            to="/profile"
            className={`mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 font-semibold text-amber-200 transition hover:bg-amber-400/20 ${
              compact ? 'py-2 text-xs' : 'py-3 text-sm'
            }`}
          >
            Editar Perfil e Atualizar Foto
          </Link>
          <button
            type="button"
            className={`mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 font-semibold text-white transition hover:bg-white/10 ${
              compact ? 'py-2 text-xs' : 'py-3 text-sm'
            }`}
            onClick={() => navigate('/my-certifications')}
          >
            Voltar para Minhas inscrições
          </button>
        </div>
      )}

      {status === 'failed' && (
        <div
          className={`rounded-2xl border border-red-400/20 bg-red-500/10 text-red-200 ${
            compact ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'
          }`}
        >
          {`Rosto não reconhecido — tentativa ${attempts.length} de ${maxAttempts}. Reposicione o rosto no oval e aguarde.`}
        </div>
      )}

      {(status === 'awaiting_selfie' || status === 'comparing') && (
        <div
          className={`rounded-2xl border border-white/10 bg-white/5 leading-5 text-slate-300 ${
            compact ? 'px-3 py-2 text-[11px]' : 'px-4 py-2 text-xs'
          }`}
        >
          <span className="font-semibold text-white">Dica:</span> certifique-se
          de que o ambiente está bem iluminado e seu rosto está centralizado.
        </div>
      )}
    </>
  );

  return (
    <div className="pointer-events-auto fixed inset-0 z-50 overflow-y-auto bg-slate-950/75 px-1.5 py-1.5 sm:px-3 sm:py-3 md:overflow-hidden">
      <div className="relative mx-auto flex max-h-[calc(100dvh-0.75rem)] w-full max-w-[1020px] flex-col overflow-hidden rounded-[1.35rem] border border-white/10 bg-slate-950/95 p-2.5 text-white shadow-2xl backdrop-blur sm:max-h-[calc(100dvh-1.5rem)] sm:rounded-[1.75rem] sm:p-4 md:block md:h-[calc(100dvh-1.5rem)] md:max-h-none md:p-5">
        {/* Background blobs */}
        <div className="pointer-events-none absolute -left-10 top-8 h-40 w-40 rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-8 right-8 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl" />

        <div className="grid min-h-0 gap-3 overflow-y-auto sm:gap-4 md:h-full md:grid-cols-[minmax(0,1.15fr),minmax(0,0.85fr)] md:overflow-visible">
          {/* Left panel — camera */}
          <div className="flex min-h-0 flex-col rounded-[1.35rem] border border-white/10 bg-slate-900/90 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] sm:rounded-[1.5rem] sm:p-4 md:h-full">
            <div className="rounded-[1.2rem] border border-white/10 bg-white/5 p-3 md:hidden">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-amber-300/20 bg-amber-300/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-200">
                  Verificação de identidade
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-semibold text-slate-200">
                  {stepLabel}
                </span>
              </div>
              <h2 className="mt-3 text-xl font-semibold leading-tight text-white">
                Confirme sua identidade
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Posicione o rosto no oval da câmera. A foto será tirada
                automaticamente quando o enquadramento estiver correto.
              </p>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 md:mt-0">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-300">
                Câmera ao vivo
              </p>
              <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-slate-200">
                {badgeLabel}
              </span>
            </div>

            <p className="mt-2 text-sm leading-5 text-slate-300 md:mt-3 md:leading-6">
              {statusCopy}
            </p>

            <div className="relative mt-3 h-[42dvh] min-h-[260px] max-h-[430px] sm:h-[50dvh] sm:min-h-[320px] sm:max-h-[560px] md:h-auto md:max-h-none md:min-h-0 md:flex-1">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="pointer-events-none absolute opacity-0"
                style={{ width: 1, height: 1 }}
              />

              {isComparing ? (
                <div className="flex h-full min-h-[200px] items-center justify-center rounded-[1.5rem] border border-white/10 bg-slate-950 sm:min-h-[280px] md:min-h-0">
                  <div className="flex flex-col items-center gap-4">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-cyan-400" />
                    <p className="text-sm font-semibold text-cyan-300">
                      Verificando identidade...
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  className={
                    status === 'awaiting_selfie' || status === 'failed'
                      ? 'h-full shadow-[0_0_0_4px_rgba(34,211,238,0.12)]'
                      : 'h-full'
                  }
                >
                  <FaceGuide
                    progress={
                      status === 'loading_models' || isBlocked || isNoPhoto
                        ? 0
                        : alignmentProgress
                    }
                    stream={stream}
                    mode="fill"
                    instructionText="Alinhe o rosto no oval"
                    instructionContainerClassName="top-3 px-3 sm:top-10 sm:px-0"
                    instructionBadgeClassName="max-w-[calc(100%-1.5rem)] px-4 py-1.5 text-center text-xs normal-case tracking-[0.08em] sm:max-w-none sm:px-3 sm:py-1 sm:text-[11px] sm:uppercase sm:tracking-[0.25em]"
                  />
                </div>
              )}
            </div>

            <div className="mt-3 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-3 py-2.5 text-xs leading-5 text-cyan-100 sm:px-4 sm:text-sm">
              Mantenha seu rosto visível, bem iluminado e alinhado.
            </div>

            {showManualCaptureButton && (
              <button
                type="button"
                className="mt-2 hidden w-full rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-400/20 md:block"
                onClick={triggerCapture}
              >
                Tirar foto agora
              </button>
            )}

            <div className="mt-3 grid gap-3 md:hidden">
              <div className="rounded-[1.2rem] border border-white/10 bg-slate-900/70 p-3">
                <div
                  className={`grid gap-3 ${
                    showProfilePreview ? 'grid-cols-[auto,1fr]' : 'grid-cols-1'
                  }`}
                >
                  {showProfilePreview && (
                    <div className="min-w-0">
                      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        Foto do perfil
                      </p>
                      <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-slate-800">
                        {resolvedProfileBlobUrl ? (
                          <img
                            src={resolvedProfileBlobUrl}
                            alt="Foto do perfil"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-slate-300" />
                        )}
                      </div>
                    </div>
                  )}

                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Como funciona
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-slate-200">
                      Aguarde a detecção estabilizar. Se preferir, a captura
                      manual aparece automaticamente depois de alguns segundos.
                    </p>

                    {!isBlocked && !isNoPhoto && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-200">
                          {remainingAttempts === 1
                            ? 'Última tentativa'
                            : `${remainingAttempts} tentativas restantes`}
                        </span>
                        <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-200">
                          Captura automática
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>{renderStatusSpecifics(true)}</div>

              {(showManualCaptureButton || onBack) && (
                <div className="grid gap-2 border-t border-white/10 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.25rem)]">
                  {showManualCaptureButton && (
                    <button
                      type="button"
                      className="w-full rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-3 text-sm font-semibold text-cyan-200 transition hover:bg-cyan-400/20"
                      onClick={triggerCapture}
                    >
                      Tirar foto agora
                    </button>
                  )}

                  {onBack && (
                    <button
                      type="button"
                      onClick={onBack}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                    >
                      Voltar
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right panel — info */}
          <div className="hidden min-h-0 flex-col rounded-[1.5rem] border border-white/10 bg-slate-900/70 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] md:flex md:h-full">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-amber-300/20 bg-amber-300/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-200">
                  Verificação de identidade
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold text-slate-200">
                  {stepLabel}
                </span>
              </div>
              <h2 className="mt-3 text-xl font-semibold text-white sm:text-2xl">
                Confirme sua identidade
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                Posicione o rosto no oval da câmera. A foto será tirada
                automaticamente quando o enquadramento estiver correto.
              </p>
            </div>

            <div className="mt-3 flex flex-1 flex-col justify-start gap-3 sm:gap-4">
              {/* Profile photo */}
              {status !== 'no_profile_photo' && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Foto do perfil
                  </p>
                  <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-slate-800 sm:h-32 sm:w-32">
                    {resolvedProfileBlobUrl ? (
                      <img
                        src={resolvedProfileBlobUrl}
                        alt="Foto do perfil"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-600 border-t-slate-300" />
                    )}
                  </div>
                </div>
              )}

              {renderStatusSpecifics(false)}

              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="mt-4 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  Voltar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
